import { useEffect, useState } from 'react';
import { Smartphone, Loader2, Download } from 'lucide-react';
import { api } from '../lib/spizirnaUi';

/** „před 5 min" / „včera 14:20" / „12. 10. 9:05" */
function kdy(iso) {
    if (!iso) return '—';
    const d = new Date(iso.replace(' ', 'T') + (iso.includes('Z') || iso.includes('+') ? '' : 'Z'));
    const min = Math.round((Date.now() - d.getTime()) / 60000);
    if (min < 1) return 'teď';
    if (min < 60) return `před ${min} min`;
    const hod = d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    const dnes = new Date();
    const vcera = new Date(); vcera.setDate(dnes.getDate() - 1);
    if (d.toDateString() === dnes.toDateString()) return `dnes ${hod}`;
    if (d.toDateString() === vcera.toDateString()) return `včera ${hod}`;
    return `${d.getDate()}. ${d.getMonth() + 1}. ${hod}`;
}

/**
 * Stav telefonu proti tomu, co je na serveru:
 *   nové APK — úroveň nativní části je nižší, balíčky už nedostává,
 *   čeká    — má jiný balíček, nový se použije, až aplikace půjde do pozadí,
 *   aktuální.
 */
function stav(z, server) {
    if (z.nativni_uroven != null && server.uroven && z.nativni_uroven < server.uroven) {
        return { text: 'Potřebuje nové APK', barva: '#EF4444' };
    }
    if (z.balicek && server.balicek && z.balicek !== server.balicek) {
        return { text: 'Čeká na aktualizaci', barva: '#F59E0B' };
    }
    if (!z.balicek && !z.nove_id) {
        return { text: 'Úroveň v pořádku', barva: '#22C55E' };
    }
    return { text: 'Aktuální', barva: '#22C55E' };
}

/**
 * Telefony s mobilní aplikací (2026-10-23): kdo ji má, na jakém telefonu
 * a v jaké verzi. Telefon se zapíše sám při každé kontrole aktualizace,
 * i se starou verzí aplikace (ta se pozná jen podle modelu telefonu).
 */
export default function AplikaceTelefony({ isDark, muted, border, kartaBg }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        const nacti = () => api('/api/aplikace-zarizeni').then(setData).catch(() => setData({ server: {}, zarizeni: [] }));
        nacti();
        const t = setInterval(nacti, 60000);
        return () => clearInterval(t);
    }, []);

    return (
        <div className="rounded-xl" style={{ background: kartaBg, border: `1px solid ${border}` }}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3" style={{ borderBottom: `1px solid ${border}` }}>
                <Smartphone size={16} style={{ color: muted }} />
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Aplikace v telefonech</span>
                {data?.server?.uroven ? (
                    <span className="text-[11px]" style={{ color: muted }}>
                        Na serveru: APK úroveň {data.server.uroven}{data.server.balicek ? ` · balíček ${data.server.balicek}` : ''}
                    </span>
                ) : null}
                <a href="/auth/aplikace/stahnout" className="ml-auto flex items-center gap-1 text-[11px] font-semibold"
                    style={{ color: 'var(--text-secondary)', textDecoration: 'none', minHeight: 32 }}>
                    <Download size={12} /> Stáhnout APK
                </a>
            </div>

            {!data ? (
                <div className="px-4 py-3"><Loader2 size={16} className="animate-spin" style={{ color: muted }} /></div>
            ) : data.zarizeni.length === 0 ? (
                <p className="px-4 py-3 text-xs" style={{ color: muted, margin: 0 }}>
                    Zatím se neozval žádný telefon. Zapíše se sám, jakmile v něm někdo aplikaci otevře.
                </p>
            ) : (
                <div className="flex flex-col">
                    {data.zarizeni.map((z, i) => {
                        const s = stav(z, data.server);
                        const telefon = [
                            z.platforma === 'ios' ? 'iOS' : z.platforma === 'android' ? 'Android' : null,
                            z.os_verze,
                        ].filter(Boolean).join(' ');
                        const verze = [
                            z.apk_verze && `APK ${z.apk_verze}`,
                            z.nativni_uroven != null && `úroveň ${z.nativni_uroven}`,
                            z.balicek && `balíček ${z.balicek}`,
                        ].filter(Boolean).join(' · ');
                        const online = z.naposledy_v && (Date.now() - new Date(z.naposledy_v.replace(' ', 'T') + 'Z').getTime()) < 10 * 60000;
                        return (
                            <div key={z.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5"
                                style={{ borderTop: i ? `1px solid ${border}` : 'none' }}>
                                <span style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, background: online ? '#22C55E' : (isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)') }}
                                    title={online ? 'Aktivní v posledních 10 minutách' : 'Neaktivní'} />
                                <div className="min-w-0 flex-1" style={{ minWidth: 180 }}>
                                    <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                                        {z.kdo || 'Nepřihlášený'}
                                        <span className="font-normal" style={{ color: muted }}> · {[telefon, z.model].filter(Boolean).join(' · ') || 'neznámý telefon'}</span>
                                    </div>
                                    <div className="text-[11px] truncate" style={{ color: muted }}>
                                        {verze || 'verze neznámá'}
                                        {!z.nove_id && ' · starší aplikace (neposílá vlastní id)'}
                                    </div>
                                </div>
                                <span className="text-[11px] font-semibold" style={{ color: s.barva }}>{s.text}</span>
                                <span className="text-[11px] tabular-nums" style={{ color: muted, minWidth: 90, textAlign: 'right' }}>{kdy(z.naposledy_v)}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
